import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CalendarClock, Plus, Trash2, X, Lock, Unlock } from "lucide-react";

const err = (e) => {
    const d = e.response?.data?.detail;
    return Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Something went wrong";
};
export const fmtTime = (hm) => {
    const [h, m] = hm.split(":").map(Number);
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
export const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

const EMPTY = { class_id: "", date: "", start_time: "14:00", end_time: "15:00", slot_minutes: 15, location: "", note: "", audience: "class" };

function NewConference({ classes, onDone }) {
    const [open, setOpen] = React.useState(false);
    const [f, setF] = React.useState(EMPTY);
    const [busy, setBusy] = React.useState(false);
    const set = (k) => (v) => setF(p => ({ ...p, [k]: v?.target ? v.target.value : v }));
    React.useEffect(() => {
        if (open && !f.class_id && classes[0]) { setF(p => ({ ...p, class_id: classes[0].id })); }
    }, [open, classes, f.class_id]);

    async function submit() {
        setBusy(true);
        try {
            await api.post("/conferences", { ...f, location: f.location || null, note: f.note || null });
            toast.success("Conference opened — class parents notified in the app and by email");
            setOpen(false); setF(EMPTY); onDone();
        } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
    }
    return (
        <>
            <Button onClick={() => setOpen(true)} className="gap-1.5" data-testid="conference-new"><Plus className="w-4 h-4" /> Open conference times</Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="conference-dialog">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Open conference times</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Class</Label>
                            <Select value={f.class_id} onValueChange={set("class_id")}>
                                <SelectTrigger data-testid="conference-class"><SelectValue placeholder="Class" /></SelectTrigger>
                                <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="conference-date" /></div>
                            <div><Label>From</Label><Input type="time" value={f.start_time} onChange={set("start_time")} data-testid="conference-start" /></div>
                            <div><Label>To</Label><Input type="time" value={f.end_time} onChange={set("end_time")} data-testid="conference-end" /></div>
                        </div>
                        <div><Label>Minutes per parent</Label>
                            <div className="flex gap-2 mt-1">
                                {[10, 15].map(m => (
                                    <Button key={m} type="button" variant={f.slot_minutes === m ? "default" : "outline"} onClick={() => set("slot_minutes")(m)} data-testid={`conference-slot-${m}`}>{m} minutes</Button>
                                ))}
                            </div>
                        </div>
                        <div><Label>Where (optional)</Label><Input value={f.location} onChange={set("location")} placeholder="e.g. Room 4" data-testid="conference-location" /></div>
                        <div><Label>Message to parents (optional)</Label><Textarea rows={2} value={f.note} onChange={set("note")} data-testid="conference-note" /></div>
                        <p className="text-xs text-brand-stone">Parents of this class get an in-app notice and an email.</p>
                        <Button onClick={submit} disabled={busy || !f.class_id || !f.date} className="w-full" data-testid="conference-submit">{busy ? "Opening…" : "Open & notify parents"}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SlotRow({ s, minutes, onCancel }) {
    const b = s.booking;
    return (
        <div className="flex items-center gap-3 py-1.5 text-sm border-b border-border/50 last:border-0" data-testid={`teacher-slot-${s.start}`}>
            <span className="w-20 font-medium">{fmtTime(s.start)}</span>
            {b ? (
                <>
                    <span className="flex-1 min-w-0 truncate">{b.parent_name}{b.student_name ? ` · ${b.student_name}` : ""}<span className="text-brand-stone text-xs"> {b.parent_phone || b.parent_email}</span></span>
                    <Button variant="ghost" size="sm" onClick={() => onCancel(b)} className="text-destructive" data-testid={`teacher-cancel-${b.id}`}><X className="w-3.5 h-3.5" /></Button>
                </>
            ) : <span className="flex-1 text-brand-stone text-xs">Open · {minutes} min</span>}
        </div>
    );
}

function TeacherConferenceCard({ c, reload }) {
    async function toggle() {
        await api.patch(`/conferences/${c.id}`, { status: c.status === "open" ? "closed" : "open" });
        toast.success(c.status === "open" ? "Booking closed" : "Booking reopened");
        reload();
    }
    async function remove() {
        if (!window.confirm("Delete these conference times? Booked parents will be emailed.")) return;
        await api.delete(`/conferences/${c.id}`);
        toast.success("Conference deleted"); reload();
    }
    async function cancel(b) {
        if (!window.confirm(`Cancel ${b.parent_name}'s booking? They'll be emailed.`)) return;
        await api.delete(`/conference-bookings/${b.id}`);
        toast.success("Booking cancelled"); reload();
    }
    return (
        <Card data-testid={`teacher-conference-${c.id}`}>
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div>
                        <div className="font-medium">{fmtDate(c.date)} · {fmtTime(c.start_time)}–{fmtTime(c.end_time)}</div>
                        <div className="text-xs text-brand-stone">{c.class_name} · {c.slot_minutes} min each · {c.booked_count}/{c.slots.length} booked{c.location ? ` · ${c.location}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <Badge className={`border-0 ${c.status === "open" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>{c.status === "open" ? "Open" : "Closed"}</Badge>
                        <Button variant="ghost" size="sm" onClick={toggle} data-testid={`conference-toggle-${c.id}`}>{c.status === "open" ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</Button>
                        <Button variant="ghost" size="sm" onClick={remove} className="text-destructive" data-testid={`conference-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                </div>
                {c.slots.map(s => <SlotRow key={s.start} s={s} minutes={c.slot_minutes} onCancel={cancel} />)}
            </CardContent>
        </Card>
    );
}

/** Teacher: open conference time frames and see bookings. */
export function TeacherConferences({ classes }) {
    const [items, setItems] = React.useState([]);
    const load = React.useCallback(() => { api.get("/conferences/mine").then(r => setItems(r.data)); }, []);
    React.useEffect(() => { load(); }, [load]);
    return (
        <div data-testid="teacher-conferences">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /><h2 className="font-dash text-xl font-semibold">Parent-teacher conferences</h2></div>
                <NewConference classes={classes} onDone={load} />
            </div>
            <div className="space-y-3">
                {items.map(c => <TeacherConferenceCard key={c.id} c={c} reload={load} />)}
                {items.length === 0 && <Card><CardContent className="p-10 text-center text-brand-stone text-sm">No conferences yet. Open a time frame when you're ready to meet parents.</CardContent></Card>}
            </div>
        </div>
    );
}
